import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  rectSortingStrategy,
  useSortable,
  arrayMove,
  defaultAnimateLayoutChanges,
  type AnimateLayoutChanges,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useRef } from "react";
import { X } from "lucide-react";

type Img = { id: string; orden: number; thumbUrl: string };

// Forzamos animar el cambio de layout del item recién arrastrado → al soltar
// DESLIZA a su nueva posición en vez de pegar el saltito (bug al mover der→izq).
const animateLayoutChanges: AnimateLayoutChanges = (args) =>
  defaultAnimateLayoutChanges({ ...args, wasDragging: true });

/**
 * Desfase del wiggle a partir del ID, no de la posición.
 *
 * Con el desfase atado al índice, al soltar una miniatura en otro lugar su fase
 * saltaba de golpe y se veía un tirón justo en el momento del drop — lo que
 * parecía un bug de la animación. Derivándolo del id, cada miniatura conserva su
 * fase aunque cambie de posición y el reordenamiento queda fluido.
 */
function desfaseWiggle(id: string): string {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) % 55;
  return `-${(h / 100).toFixed(2)}s`;
}

// Una miniatura arrastrable (toda la miniatura es el handle). Hace un "wiggle"
// sutil para señalar que se puede mover; la × (quitar) va adentro arriba a la
// derecha. El orden es izquierda→derecha (sin números, se entiende solo).
function SortableThumb({
  img,
  index,
  onRemove,
  grande,
  accionExtra,
  onVer,
}: {
  img: Img;
  index: number;
  onRemove: (id: string) => void;
  grande?: boolean;
  accionExtra?: (id: string) => React.ReactNode;
  onVer?: (id: string) => void;
}) {
  // Dónde empezó el gesto. dnd-kit deja pasar el `click` después de arrastrar
  // —para él el arrastre terminó y el navegador dispara el click igual—, así que
  // sin esto soltar una miniatura en otro lugar abriría el visor.
  const desde = useRef<{ x: number; y: number } | null>(null);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: img.id, animateLayoutChanges });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
  };
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="relative shrink-0 cursor-grab active:cursor-grabbing touch-none"
    >
      <div
        onPointerDown={(e) => {
          desde.current = { x: e.clientX, y: e.clientY };
        }}
        onClick={(e) => {
          if (!onVer) return;
          const p = desde.current;
          // Más de 5px de recorrido fue un arrastre, no un click. Es el mismo
          // umbral que activa el sensor.
          if (p && Math.hypot(e.clientX - p.x, e.clientY - p.y) > 5) return;
          onVer(img.id);
        }}
        className={`relative ${grande ? "w-28 h-28 rounded-xl" : "w-16 h-16 rounded-md"} overflow-hidden shadow-sm ${
          onVer ? "cursor-zoom-in" : ""
        }`}
        style={{
          // el que se arrastra no hace wiggle (queda derecho mientras lo movés)
          animation: isDragging ? "none" : "tn-wiggle 0.55s ease-in-out infinite",
          animationDelay: desfaseWiggle(img.id),
        }}
      >
        <img
          src={img.thumbUrl}
          alt=""
          className="w-full h-full object-cover pointer-events-none"
        />
      </div>
      <span
        className={`absolute z-10 rounded-full bg-brand-primary text-white inline-flex items-center justify-center font-semibold ${
          grande ? "top-1 left-1 w-5 h-5 text-[11px]" : "top-0.5 left-0.5 w-4 h-4 text-[10px]"
        }`}
      >
        {index + 1}
      </span>
      <button
        type="button"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onRemove(img.id)}
        className={`absolute rounded-full bg-black/55 text-white inline-flex items-center justify-center hover:bg-black/75 z-10 ${
          grande ? "top-1 right-1 w-6 h-6" : "top-0.5 right-0.5 w-4 h-4"
        }`}
      >
        <X className={grande ? "w-3.5 h-3.5" : "w-2.5 h-2.5"} />
      </button>
      {/* Acción propia del que usa la lista, abajo a la derecha: es la esquina
          que queda libre (arriba están el número y la ×). El `stopPropagation`
          es el mismo que necesita la ×: sin él, apretar el botón arranca un
          arrastre y el click nunca llega. */}
      {accionExtra && (
        <span
          onPointerDown={(e) => e.stopPropagation()}
          className={`absolute z-10 ${grande ? "bottom-1 right-1" : "bottom-0.5 right-0.5"}`}
        >
          {accionExtra(img.id)}
        </span>
      )}
    </div>
  );
}

// Grilla de miniaturas arrastrables (drag-and-drop) para reordenar las fotos.
// El orden vive afuera; emitimos el nuevo orden de ids al soltar.
export function SortableImageList({
  imagenes,
  onReorder,
  onRemove,
  grande,
  accionExtra,
  onVer,
}: {
  imagenes: Img[];
  onReorder: (idsEnOrden: string[]) => void;
  onRemove: (id: string) => void;
  /** Miniaturas grandes: la ficha del lanzamiento las muestra como protagonistas. */
  grande?: boolean;
  /** Botón extra en la esquina de abajo a la derecha de cada miniatura. Lo usa la
   *  ficha para mover una foto a otra variante. */
  accionExtra?: (id: string) => React.ReactNode;
  /** Tocar la miniatura (sin arrastrarla) la abre ampliada. Sin esto, la
   *  miniatura solo se arrastra. */
  onVer?: (id: string) => void;
}) {
  // distance:5 → distingue tap de drag (anda en mouse y touch).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );
  const ids = imagenes.map((i) => i.id);
  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (over && active.id !== over.id) {
      const oldIndex = ids.indexOf(String(active.id));
      const newIndex = ids.indexOf(String(over.id));
      if (oldIndex !== -1 && newIndex !== -1) {
        onReorder(arrayMove(ids, oldIndex, newIndex));
      }
    }
  };
  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <style>
        {"@keyframes tn-wiggle{0%,100%{transform:rotate(-1.6deg)}50%{transform:rotate(1.6deg)}}"}
      </style>
      <SortableContext items={ids} strategy={rectSortingStrategy}>
        <div className="flex flex-wrap gap-3 py-1">
          {imagenes.map((img, i) => (
            <SortableThumb
              key={img.id}
              img={img}
              index={i}
              onRemove={onRemove}
              grande={grande}
              accionExtra={accionExtra}
              onVer={onVer}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

export default SortableImageList;
